import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/apiClient';

const TrustAdminLogin = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [email, setEmail] = useState('iicontact@cloudai.in');
  const [loginError, setLoginError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  // New state to support admin comments (no access condition — page is admin-only)
  const [newComments, setNewComments] = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Helper to format possible timestamp shapes safely
  const formatDateTime = (time) => {
    try {
      if (!time) return '-';
      const ts = (time && time.$date) ? time.$date : time;
      const d = new Date(ts);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch (e) {
      return '-';
    }
  };

  // Helper to extract numeric timestamp for sorting (returns 0 when invalid)
  const getTimestamp = (time) => {
    try {
      if (!time) return 0;
      const ts = (time && time.$date) ? time.$date : time;
      const d = new Date(ts);
      if (isNaN(d.getTime())) return 0;
      return d.getTime();
    } catch (e) {
      return 0;
    }
  };

  const handleGoogleLogin = () => {
    if (email === 'iicontact@cloudai.in') {
      setIsLoggedIn(true);
      setLoginError('');
    } else {
      setLoginError('Invalid email. Please use valid credentials.');
    }
  };

  const handleDelete = async (jobRunId) => {
    try {
      await apiClient.delete(`/api/v2/jobs/run/${jobRunId}`);
      setJobs(jobs.filter(job => job.jobRunId !== jobRunId));
    } catch (err) {
      const errorMessage = err.response ? `${err.response.status}: ${err.response.data.message}` : err.message;
      setError(`Failed to delete job. Server says: ${errorMessage}`);
      console.error(err);
    }
  };

  const handleJobClick = async (jobRunId) => {
    setIsDetailsLoading(true);
    try {
      const response = await apiClient.get(`/api/v2/jobs/run/${jobRunId}`);
      setSelectedJob(response.data);
    } catch (err) {
      setError('Failed to fetch job details.');
      console.error(err);
    }
    setIsDetailsLoading(false);
  };

  // Helpers for admin comments
  const handleCommentChange = (taskName, text) => {
    setNewComments(prev => ({ ...prev, [taskName]: text }));
  };

  const handleAddComment = async (e, taskName) => {
    e.preventDefault();
    setSubmitError(null);
    const commentText = (newComments[taskName] || '').trim();
    if (!commentText) return;

    try {
      const response = await apiClient.post(
        `/api/v2/jobs/run/${selectedJob.jobRunId}/tasks/${taskName}/comments`,
        { text: commentText, userId: 'Admin' }
      );

      // API should return the created comment. If not, build a fallback comment object.
      const created = response && response.data ? response.data : { id: Date.now(), text: commentText, userId: 'Admin', timestamp: new Date().toISOString() };

      setSelectedJob(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => (t.taskName === taskName ? { ...t, comments: [created, ...(t.comments || [])] } : t)),
      }));

      setNewComments(prev => ({ ...prev, [taskName]: '' }));
    } catch (err) {
      console.error('Failed to add admin comment', err);
      setSubmitError('Failed to submit comment. Please try again.');
    }
  };

  const handleBackToList = () => {
    setSelectedJob(null);
    setError(null);
  };

  const renderCommentText = (text) => {
    if (!text) return '';
    const segments = String(text).split(/(https?:\/\/[^\s]+|www\.[^\s]+)/gi);
    return segments.map((segment, index) => {
      if (!segment) return null;
      const isUrl = /^https?:\/\//i.test(segment) || /^www\./i.test(segment);
      if (!isUrl) {
        return <React.Fragment key={`${segment}-${index}`}>{segment}</React.Fragment>;
      }
      const href = /^www\./i.test(segment) ? `https://${segment}` : segment;
      return (
        <a
          key={`${segment}-${index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {segment}
        </a>
      );
    });
  };

  useEffect(() => {
    if (isLoggedIn) {
      const fetchJobs = async () => {
        setIsLoading(true);
        try {
          const response = await apiClient.get('/api/v2/jobs');
          // Sort jobs by startTime (created date) descending so newest appear first
          const sorted = Array.isArray(response.data)
            ? response.data.slice().sort((a, b) => getTimestamp(b.startTime) - getTimestamp(a.startTime))
            : response.data;
          setJobs(sorted);
        } catch (err) {
          setError('Failed to fetch jobs.');
          console.error(err);
        }
        setIsLoading(false);
      };

      fetchJobs();
    }
  }, [isLoggedIn]);

  // Compact table styles for a tighter, more condensed list view
  const compact = {
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '0.92rem' },
    th: { border: '1px solid #ddd', padding: '6px 8px', textAlign: 'left', fontSize: '0.9rem', background: '#fff' },
    td: { border: '1px solid #ddd', padding: '6px 8px', verticalAlign: 'middle' },
    deleteBtn: { padding: '4px 8px', fontSize: '12px', cursor: 'pointer', backgroundColor: 'red', color: 'white', border: 'none', borderRadius: '4px' },
    idLink: { textDecoration: 'underline', cursor: 'pointer', fontSize: '0.92rem' }
  };

  // Get current user email from localStorage to authorize admin access
  const userStr = localStorage.getItem("user");
  let appUserEmail = "";
  try {
    const userObj = userStr ? JSON.parse(userStr) : null;
    appUserEmail = userObj?.email || "";
  } catch (e) {
    console.error("Failed to parse user from localStorage", e);
  }

  const isAuthorized = appUserEmail === 'contact@cloudai.in' || appUserEmail === 'sonuojha83@gmail.com';

  if (!isAuthorized) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
        <h2 style={{ color: '#f44336' }}>Access Denied</h2>
        <p style={{ color: '#555' }}>You do not have permission to view the admin page.</p>
        <button 
          onClick={() => window.history.back()} 
          style={{ 
            padding: '10px 20px', 
            cursor: 'pointer', 
            marginTop: '20px',
            backgroundColor: '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontWeight: '600'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {!isLoggedIn ? (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <input
            type="email"
            placeholder="Enter your Gmail address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ padding: '10px', marginBottom: '10px', width: '300px' }}
          />
          <button onClick={handleGoogleLogin} style={{ padding: '15px 30px', fontSize: '18px', cursor: 'pointer' }}>
            Login with Gmail
          </button>
          {loginError && <p style={{ color: 'red', marginTop: '10px' }}>{loginError}</p>}
        </div>
      ) : selectedJob ? (
        <div>
          <button onClick={handleBackToList} style={{ marginBottom: '20px', padding: '10px 15px', cursor: 'pointer' }}>
            &larr; Back to List
          </button>
          <h2>{`Ticket #${selectedJob.jobRunId.substring(4, 10)} - ${selectedJob.jobTitle}`}</h2>
          <div className="meta">
            Status: <span>{selectedJob.status}</span> | Created By: <span>{selectedJob.createdBy}</span>
            <br />
            Created Date: <span>{formatDateTime(selectedJob.startTime)}</span>
          </div>
          <p>{selectedJob.jobDescription}</p>
          <div className="comments">
            <h3>Tasks</h3>
            {selectedJob.tasks.map((task, index) => (
              <div key={index} className="comment">
                <strong>{`${task.taskName} (Status: ${task.status})`}</strong>
                <p style={{ margin: '5px 0 15px 0' }}>{task.taskDescription}</p>
                <div style={{ marginLeft: '20px', marginTop: '10px', fontSize: '0.9em', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                  <strong>Comments:</strong>
                  {task.comments && task.comments.length > 0 ? (
                    task.comments.map((comment, commentIdx) => (
                      <div key={comment.id || commentIdx} style={{ marginTop: '5px' }}>
                        {'• '}{renderCommentText(comment.text)}
                        {comment.userId && <em>{` (by ${comment.userId} at ${formatDateTime(comment.timestamp)})`}</em>}
                      </div>
                    ))
                  ) : (
                    <div style={{ fontStyle: 'italic', color: '#888', marginTop: '5px' }}>No comments yet.</div>
                  )}
                </div>

                {/* Admin comment input - always visible on this page (admin-only page) */}
                <div style={{ marginLeft: '20px', marginTop: '12px' }}>
                  <form onSubmit={(e) => handleAddComment(e, task.taskName)} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Add a comment as Admin..."
                      value={newComments[task.taskName] || ''}
                      onChange={(e) => handleCommentChange(task.taskName, e.target.value)}
                      style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                    <button type="submit" style={{ padding: '8px 12px', border: 'none', backgroundColor: '#6A1B9A', color: 'white', borderRadius: '4px', cursor: 'pointer' }}>
                      Add (Admin)
                    </button>
                  </form>
                  {submitError && <div style={{ color: 'red', marginTop: '8px' }}>{submitError}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1>Hello Admin - All Jobs</h1>
            <button onClick={() => navigate('/utility-dashboard')} style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              background: '#2b6cb0',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}>🏠 TrustApp Home</button>
          </div>
          {isLoading && <p>Loading jobs...</p>}
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {jobs.length > 0 && (
            <table style={compact.table}>
              <thead>
                <tr>
                  <th style={compact.th}>Job ID</th>
                  <th style={compact.th}>Title</th>
                  <th style={compact.th}>Status</th>
                  <th style={compact.th}>Created By</th>
                  <th style={compact.th}>Created At</th>
                  <th style={compact.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job.jobRunId}>
                    <td style={compact.td}>
                      <a href="#" onClick={() => handleJobClick(job.jobRunId)} style={compact.idLink}>
                        {job.jobRunId.substring(4, 10)}
                      </a>
                    </td>
                    <td style={compact.td}>{job.jobTitle || job.jobName}</td>
                    <td style={compact.td}>{job.status}</td>
                    <td style={compact.td}>{job.createdBy}</td>
                    <td style={compact.td}>{formatDateTime(job.startTime)}</td>
                    <td style={compact.td}>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(job.jobRunId); }} style={compact.deleteBtn}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default TrustAdminLogin;